/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */


import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';

let nextDropdownId = 0;

@Component({
    selector: 'ngf-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DropdownComponent implements OnInit, OnDestroy {

  @Input()
  panelClass: string;

  @Input()
  hasOverflowY = true;

  @Input()
  dropdownId?: string;

  /** Whether the overlay can be pushed on-screen if none of the provided positions fit. */
  @Input()
  overlayPush = false;

  @Output()
  dropdownClosed = new EventEmitter();

  @Output()
  dropdownOpened = new EventEmitter();

  @ViewChild(CdkConnectedOverlay)
  connectedOverlay: CdkConnectedOverlay;

  public triggerRef: ElementRef<HTMLElement>;
  isOpen = false;
  positions: ConnectedPosition[];
 
  elementRect: DOMRect;
  scrollStrategy: ScrollStrategy;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly overlay = inject(Overlay);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private scrollSub = Subscription.EMPTY;
  private repositionQueued = false;

  ngOnInit(): void {
    this.dropdownId = this.dropdownId || `ngf-dropdown-${nextDropdownId++}`;
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    this.positions = [
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 5
      },
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: 5
      },
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 5
      },
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: 5
      },
    ];
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('click', this.handleOutsideClick);
    });
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    if (!this.isOpen) {
      this.elementRect = this.triggerRef.nativeElement.getBoundingClientRect();
      this.isOpen = true;
      this.cdr.markForCheck();
      this.dropdownOpened.emit();
      this.subscribeToScroll();
      requestAnimationFrame(() => {
        const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement;
        // Prefer an element already marked as the active roving-tabindex item (e.g. set by
        // ngfKeyboardNavList to reflect the current selection) over just the first button in
        // DOM order, so reopening a listbox-style dropdown focuses the selected option.
        const rovingActiveItem = overlayEl?.querySelector<HTMLElement>('[tabindex="0"]:not([disabled])');
        const firstFocusable = overlayEl?.querySelector<HTMLElement>('button:not([disabled])');
        (rovingActiveItem ?? firstFocusable)?.focus();
      });
    }
  }

  close(returnFocus = true): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.cdr.markForCheck();
      this.dropdownClosed.emit();
      this.scrollSub.unsubscribe();
      if (!returnFocus) {
        requestAnimationFrame(() => {
          if (this.triggerRef?.nativeElement === document.activeElement) {
            this.triggerRef.nativeElement.blur();
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleOutsideClick);
    this.scrollSub.unsubscribe();
  }

  /**
   * React to page scrolls while the dropdown is open. What "react" means depends on the viewport.
   *
   * On handset, dismiss. Keeping the overlay glued to its trigger there is wrong: the trigger
   * scrolls up underneath the sticky header and the overlay — rendered in a fixed-position,
   * high-z-index pane — follows it and covers the header.
   *
   * Everywhere else, reposition. Dismissing on desktop/tablet collapses the menu on scrolls the
   * user never aimed at it (wheel/trackpad with the pointer elsewhere), and on the
   * scroll-into-view a WebDriver performs before interacting with an element, which breaks
   * automation outright.
   *
   * cdkConnectedOverlay's reposition scroll strategy only listens for scrolls on the window
   * and on CdkScrollable-registered containers. Two cases slip through:
   *   1. Scrolls inside arbitrary sub-containers (mouse wheel / trackpad on a list inside a
   *      div without cdkScrollable) — fire scroll events that don't bubble, so CDK misses them.
   *   2. iOS Safari touch and momentum scrolling — scroll events are suppressed mid-gesture.
   * Catch (1) via a document capture-phase scroll listener (scroll doesn't bubble, but capture
   * sees every scrollable element) and (2) via touchmove.
   */
  private subscribeToScroll(): void {
    this.scrollSub.unsubscribe();
    this.scrollSub = merge(
      fromEvent(document, 'scroll', { passive: true, capture: true }),
      fromEvent(document, 'touchmove', { passive: true })
    ).subscribe((event: Event) => this.handleScroll(event));
  }

  private handleScroll(event: Event): void {
    const overlayRef = this.connectedOverlay?.overlayRef;
    if (!overlayRef) {
      return;
    }

    if (!this.isHandset()) {
      this.queueReposition();
      return;
    }

    // Scrolls/touches originating inside the overlay itself are the user working the menu,
    // not leaving it — a long option list must stay scrollable.
    if (overlayRef.overlayElement?.contains(event.target as Node)) {
      return;
    }

    // The overlay detach restores focus to the trigger (cdkTrapFocus), which scrolls the
    // trigger back into view and jumps the page. For a scroll event we know the scrolled
    // container and its offset, so preserve it across the close.
    const scrollEl = event.type === 'scroll' ? this.getScrollElement(event.target) : null;
    const scrollTop = scrollEl?.scrollTop;
    this.close(false);
    if (scrollEl) {
      requestAnimationFrame(() => {
        scrollEl.scrollTop = scrollTop ?? scrollEl.scrollTop;
      });
    }
  }

  /**
   * A real handset: a small viewport *and* a touch-primary pointer.
   *
   * The viewport test alone is not enough. `Breakpoints.Handset` matches any landscape window up
   * to 959.98px wide, so a desktop browser in a half-screen window — or a WebDriver session,
   * whose default window is 800x600 — reads as a handset and loses its dropdown on every scroll.
   * That is exactly the FLO-53992 regression this gate exists to prevent. A desktop pointer stays
   * `fine` however narrow the window gets, so requiring `coarse` confines the dismissal to the
   * devices the sticky-header overlap actually affects.
   */
  private isHandset(): boolean {
    return this.breakpointObserver.isMatched(Breakpoints.Handset)
      && this.breakpointObserver.isMatched('(pointer: coarse)');
  }

  /**
   * The element a scroll event actually moved. A scroll of the page itself targets `document`,
   * which has no scrollTop of its own — the offset lives on document.scrollingElement.
   */
  private getScrollElement(target: EventTarget | null): HTMLElement | null {
    if (target === document) {
      return document.scrollingElement as HTMLElement | null;
    }
    return target instanceof HTMLElement ? target : null;
  }

  /** Coalesce repositioning to one reflow per frame; scroll events can outpace the compositor. */
  private queueReposition(): void {
    if (this.repositionQueued) {
      return;
    }
    this.repositionQueued = true;
    requestAnimationFrame(() => {
      this.repositionQueued = false;
      this.connectedOverlay?.overlayRef?.updatePosition();
    });
  }

  private handleOutsideClick = (event: MouseEvent) => {
    this.ngZone.run(() => {
      if (this.isOpen) {
        const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement;

        // The overlay attaches asynchronously after `open()`, so a click can land
        // while `isOpen` is already true but `overlayRef` does not exist yet. With no
        // overlay element there is nothing to test the click against, so leave the
        // dropdown open rather than dereferencing undefined.
        if (!overlayEl) {
          return;
        }

        const path = event.composedPath();
        const originEl = this.triggerRef.nativeElement;

        if (!path.includes(originEl) && !path.includes(overlayEl)) {
          this.close(false);
        }
      }
    });
  }

}
